import { gql } from "@apollo/client";

// The tag picker only needs the Name, TagId and the aggregate contact count
// (used for the label and the 10-contact bulk-broadcast gate). The previously
// nested per-contact TagContacts sub-tree was unused and made this query far
// heavier than necessary, so it has been removed.
//
// The count is restricted to sendable contacts (not deleted, Subscribed or
// Presubscribed) so the "(N Contacts)" label matches the audience the send
// actually targets. Without the Status filter a tag whose members are all
// Unsubscribed would show e.g. "(3 Contacts)" yet recalculate to 0 recipients.
export const SEARCH_TAGS = gql`
	query searchTags($searchTerm: String!, $accountId: Int!) {
		msgbox_Tag(where: { _and: { Name: { _ilike: $searchTerm } } }) {
			Name
			TagId
			TagContacts_aggregate(
				where: {
					Contact: {
						IsDeleted: { _eq: false }
						Status: { _in: ["Subscribed", "Presubscribed"] }
						_and: {
							APIAccount: { APIAccountId: { _eq: $accountId } }
						}
					}
					Tag: { Name: { _ilike: $searchTerm } }
				}
			) {
				aggregate {
					count
				}
			}
		}
	}
`;

export const SEARCH_CONTACTS = gql`
	query searchContacts($searchTerm: String!, $accountId: Int!) {
		msgbox_Contact(
			where: {
				APIAccountId: { _eq: $accountId }
				# _and: { Name: { _ilike: $searchTerm } }
				_or: [
					{ Name: { _ilike: $searchTerm } }
					{ MobileNumber: { _ilike: $searchTerm } }
				]
			}
		) {
			Name
			ContactId
			MobileNumber
			Status
			APIAccount {
				APIAccountId
				Inboxes {
					InboxId
				}
			}
		}
	}
`;

export const SEARCH_TEMPLATE_MESSAGES = gql`
	query searchTemplateMessages(
		$apiAccountId: Int!
		$templateTypes: [String!]
	) {
		msgbox_Template(
			where: {
				APIAccount: { _eq: $apiAccountId }
				Status: { _eq: Approved }
				TemplateType: { Description: { _in: $templateTypes } }
			}
		) {
			Name
			TemplateText
			TemplateId
			Status
			Attachment
			ButtonMetadata
			TemplateType {
				Description
			}
		}
	}
`;

export const SEND_TEMPLATE_MESSAGE = gql`
	mutation sendTemplateMessage(
		$attachmentId: Int
		$Message: String
		$TemplateId: Int!
		$contacts: msgbox_BroadcastContact_arr_rel_insert_input!
		$account: Int!
		$followOnMessage: String
		$buttonParams: String
	) {
		insert_msgbox_Broadcast(
			objects: {
				Message: $Message
				TemplateId: $TemplateId
				BroadcastContacts: $contacts
				APIAccountId: $account
				AttachmentId: $attachmentId
				FollowOnMessage: $followOnMessage
				ButtonParams: $buttonParams
			}
		) {
			affected_rows
			returning {
				Message
				BroadcastContacts {
					ContactId
				}
			}
		}
	}
`;

export const GET_CONVERSATIONS_IDS = gql`
	query getConversationsIds($contactIds: [Int]!) {
		msgbox_Conversation(where: { ContactId: { _in: $contactIds } }) {
			ConversationId
			ContactId
		}
	}
`;

export const SEND_DEFERRED_MESSAGE = gql`
	mutation sendDeferredMessage($Message: String, $conversationId: Int!) {
		insert_msgbox_Message(
			objects: {
				BodyText: $Message
				ConversationId: $conversationId
				DeferredDelivery: true
			}
		) {
			affected_rows
		}
	}
`;

export const SEND_NOT_DEFERRED_MESSAGE = gql`
	mutation sendNotDeferredMessage($Message: String, $conversationId: Int!) {
		insert_msgbox_Message(
			objects: {
				BodyText: $Message
				ConversationId: $conversationId
				DeferredDelivery: false
			}
		) {
			affected_rows
		}
	}
`;

export const GET_CONTACTS_WITH_TAG = gql`
	query getContacts($tags: [String!], $account: Int!) {
		msgbox_Contact(
			where: {
				TagContacts: {
					Tag: { Name: { _in: $tags } }
					Contact: { APIAccountId: { _eq: $account } }
				}
			}
		) {
			ContactId
			Status
			Name
			TagContacts {
				TagId
				Tag {
					TagId
					Name
				}
			}
		}
	}
`;

// Lightweight, paginated version of GET_CONTACTS_WITH_TAG used only by the
// send path. Identical filter to the original query, but it drops the unused
// per-contact nested TagContacts/Tag sub-tree (the expensive part) and pages
// with $limit/$offset, so large tag audiences (600+) don't hit Hasura's 60s
// statement timeout. Status is still filtered downstream in JS, as before.
// IsDeleted is excluded so the audience matches the tag picker's aggregate count
// (which also filters IsDeleted) and deleted contacts are never messaged.
export const GET_CONTACT_IDS_FOR_SEND = gql`
	query getContactIdsForSend(
		$tags: [String!]
		$account: Int!
		$limit: Int!
		$offset: Int!
	) {
		msgbox_Contact(
			where: {
				IsDeleted: { _eq: false }
				TagContacts: {
					Tag: { Name: { _in: $tags } }
					Contact: { APIAccountId: { _eq: $account } }
				}
			}
			order_by: { ContactId: asc }
			limit: $limit
			offset: $offset
		) {
			ContactId
			Status
		}
	}
`;

// Lightweight, paginated version of GET_CONTACTS_WITH_TAG used by the Advanced
// tag dialog (and the step 1 presub split). Same filter as GET_CONTACTS_WITH_TAG
// but drops the unused per-contact nested TagContacts/Tag sub-tree and pages with
// $limit/$offset so large tag audiences (600+) don't hit Hasura's 60s statement
// timeout. Name is kept because the presub UI labels each removable contact.
// IsDeleted is excluded so the count matches the tag picker's aggregate; Status is
// restricted to sendable statuses so the recalculated total reflects who will
// actually receive the broadcast (Unsubscribed etc. are never counted or sent).
export const GET_CONTACTS_WITH_TAG_PAGED = gql`
	query getContactsWithTagPaged(
		$tags: [String!]
		$account: Int!
		$limit: Int!
		$offset: Int!
	) {
		msgbox_Contact(
			where: {
				IsDeleted: { _eq: false }
				Status: { _in: ["Subscribed", "Presubscribed"] }
				TagContacts: {
					Tag: { Name: { _in: $tags } }
					Contact: { APIAccountId: { _eq: $account } }
				}
			}
			order_by: { ContactId: asc }
			limit: $limit
			offset: $offset
		) {
			ContactId
			Status
			Name
		}
	}
`;

// Paginated version of GET_CONTACTS_WITHOUT_TAG. This is the account-wide
// "Doesn't contain" / "Isn't" query, so it MUST always be paged - without a
// limit it can scan the whole account and hit the statement timeout.
//
// Note: there is deliberately NO "has at least one tag" clause here. "Doesn't
// contain X" means every contact in the account that lacks X, including contacts
// with no tags at all - a `_not` over TagContacts is true for untagged contacts,
// so they are correctly included. IsDeleted/Status are filtered for the same
// reasons as GET_CONTACTS_WITH_TAG_PAGED (match the picker count; only sendable).
export const GET_CONTACTS_WITHOUT_TAG_PAGED = gql`
	query getContactsWithoutTagPaged(
		$tags: [String!]
		$account: Int!
		$limit: Int!
		$offset: Int!
	) {
		msgbox_Contact(
			where: {
				IsDeleted: { _eq: false }
				Status: { _in: ["Subscribed", "Presubscribed"] }
				APIAccountId: { _eq: $account }
				_not: { TagContacts: { Tag: { Name: { _in: $tags } } } }
			}
			order_by: { ContactId: asc }
			limit: $limit
			offset: $offset
		) {
			ContactId
			Status
			Name
		}
	}
`;

export const GET_CONTACTS_WITHOUT_TAG = gql`
	query getContacts($tags: [String!], $account: Int!) {
		msgbox_Contact(
			where: {
				APIAccountId: { _eq: $account }
				TagContacts: { Tag: { TagId: { _is_null: false } } }
				_not: { TagContacts: { Tag: { Name: { _in: $tags } } } }
			}
		) {
			ContactId
			Status
			Name
			TagContacts {
				TagId
				Tag {
					TagId
					Name
				}
			}
		}
	}
`;

/*
query getContacts($tags: [String!], $account: Int!) {
  msgbox_Contact(where: {APIAccountId: {_eq: $account}, TagContacts: {Tag: {TagId: {_is_null: false}}}, _not: {TagContacts: {Tag: {Name: {_in: $tags}}}}}) {
    ContactId
    Status
    Name
    TagContacts {
      TagId
      Tag {
        TagId
        Name
      }
    }
  }
}
*/

export const GET_PRESUB_FROM_IDS = gql`
	query getContactDetails($contactIds: [Int!]) {
		msgbox_Contact(where: { ContactId: { _in: $contactIds } }) {
			ContactId
			Status
			Name
			TagContacts {
				TagId
				Tag {
					TagId
					Name
				}
			}
		}
	}
`;

export const DELETE_TAG_CONTACT = gql`
	mutation deleteTagContact($tagId: Int!, $contactId: Int!) {
		delete_msgbox_TagContact(
			where: { TagId: { _eq: $tagId }, ContactId: { _eq: $contactId } }
		) {
			affected_rows
			returning {
				TagId
				ContactId
			}
		}
	}
`;

export const GET_ORG_ID = gql`
	query getOrgId {
		msgbox_Organisation {
			OrganisationId
		}
	}
`;

export const GET_API_ACCOUNT_ID = gql`
	query getApiAccountId($inboxId: Int!) {
		msgbox_Inbox(where: { InboxId: { _eq: $inboxId } }) {
			APIAccountId
		}
	}
`;

export const CREATE_NEW_CONTACTS = gql`
	mutation CreateNewContacts($objects: [msgbox_Contact_insert_input!]!) {
		insert_msgbox_Contact(objects: $objects) {
			affected_rows
			returning {
				ContactId
			}
		}
	}
`;

export const GET_LATEST_TEMPLATES = gql`
	query GetLatestTemplates($ids: [Int!], $_gt: timestamptz = "") {
		msgbox_Message(
			where: {
				ConversationId: { _in: $ids }
				IsTemplate: { _eq: true }
				DateTime: { _gt: $_gt }
			}
		) {
			BroadcastContacts {
				ContactId
			}
			MessageId
		}
	}
`;

export const GET_LATEST_INBOUND_MESSAGES = gql`
	query getLatestInboundMessages($ids: [Int!], $_gt: timestamptz = "") {
		msgbox_Message(
			where: {
				ConversationId: { _in: $ids }
				SenderId: { _is_null: true }
				DateTime: { _gt: $_gt }
			}
			limit: 1
			order_by: { DateTime: desc }
		) {
			DateTime
			MessageId
			ConversationId
			Conversation {
				Contact {
					ContactId
					Status
				}
			}
		}
	}
`;

export const GET_INBOX_NATURAL_CHAT_1 = gql`
	query getNaturalChat1($accountId: Int!) {
		msgbox_UserInbox(
			where: { Inbox: { APIAccountId: { _eq: $accountId } } }
		) {
			Inbox {
				APIAccount {
					APIAccountId
					Templates(
						where: {
							Name: { _eq: "natural_chat" }
							Status: { _eq: Approved }
						}
					) {
						TemplateId
						TemplateText
					}
				}
				InboxId
				Name
			}
		}
	}
`;

export const GET_RESELLER_NAME = gql`
	query MyQuery($orgId: Int!) {
		msgbox_Organisation(where: { OrganisationId: { _eq: $orgId } }) {
			Reseller {
				Brand
				Name
			}
		}
	}
`;

export const CHECK_MIME_TYPE = gql`
	query MyQuery($attachmentId: Int!) {
		msgbox_Attachment(where: { AttachmentId: { _eq: $attachmentId } }) {
			MimeType
		}
	}
`;

export const GET_CONVERSATION_BY_CONTACT = gql`
	query MyQuery($contactId: [Int!]) {
		msgbox_Conversation(where: { ContactId: { _in: $contactId } }) {
			ConversationId
		}
	}
`;

export const GET_SUBSCRIBERS_CONFIG = gql`
	query getSubscribersConfig {
		msgbox_Configuration {
			ConfigurationId
			TreatPresubscribersAsSubscribers
		}
	}
`;
